export default function sortByPriority<T>(
	list: T[],
	keys: (item: T) => string[],
	priorityWords: string[],
): T[] {
	// Precompute lowercase version of the priority words once.
	const lowerCasePriorityWords = priorityWords.map((word) =>
		word.toLowerCase(),
	);

	// Helper to determine the priority index based on the lowercased label.
	const getPriorityIndex = (label: string): number => {
		const lowerCaseLabel = label.toLowerCase();
		return lowerCasePriorityWords.findIndex((word) =>
			lowerCaseLabel.startsWith(word),
		);
	};

	const compareItems = (a: T, b: T): number => {
		const keysA = keys(a);
		const keysB = keys(b);
		const maxLength = Math.max(keysA.length, keysB.length);

		for (let i = 0; i < maxLength; i++) {
			const keyA = keysA[i];
			const keyB = keysB[i];

			// Handle cases where keys might be missing.
			if (keyA === undefined && keyB === undefined) continue;
			if (keyA === undefined) return 1;
			if (keyB === undefined) return -1;

			// Only compare if the keys are not identical.
			if (keyA !== keyB) {
				const prioA = getPriorityIndex(keyA);
				const prioB = getPriorityIndex(keyB);

				// If both keys match a priority word, compare their priority indices.
				if (prioA !== -1 && prioB !== -1) {
					if (prioA !== prioB) return prioA - prioB;
					// If they share the same priority, use locale comparison.
					const localeComparison = keyA.localeCompare(keyB);
					if (localeComparison !== 0) return localeComparison;
				} else if (prioA !== -1) {
					return -1; // keyA has priority while keyB does not.
				} else if (prioB !== -1) {
					return 1; // keyB has priority while keyA does not.
				} else {
					// Neither key is prioritized; compare them lexicographically.
					const localeComparison = keyA.localeCompare(keyB);
					if (localeComparison !== 0) return localeComparison;
				}
			}
		}
		// If all compared keys are equal, return 0.
		return 0;
	};

	return [...list].sort(compareItems);
}
